import {requestAchSetupIntent, requestPlaidLinkToken} from "../request/payment-method-requests";
import Busy from "../../Busy";
import {loadPlaidScript} from "../../../util/PaymentProcessorSetupUtil";

interface PlaidEnv {
    plaidEnvironment: string;
    plaidClientName: string;
    plaidPublicKey: string;
}

interface PlaidAuthority {
    id: string;
    userType: string;
}

interface PlaidHandler {
    open: () => void;
    exit: () => void;
}

interface PlaidSuccessData {
    plaidPublicToken: string;
    plaidAccountId: string;
}

interface AchFailureResult {
    requiresAction?: boolean;
    clientSecret?: string;
    message?: string;
}

interface PlaidLinkAccount {
    id: string;
    name: string;
    mask: string | null;
    type: string;
    subtype: string;
}

interface PlaidLinkOnSuccessMetadata {
    accounts: PlaidLinkAccount[];
    institution: { institution_id: string; name: string } | null;
    link_session_id: string;
}

interface PlaidCreateConfig {
    token: string;
    onSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => void;
    onExit: () => void;
}

interface PlaidInstance {
    create: (config: PlaidCreateConfig) => PlaidHandler;
}

/**
 * Opens Plaid Link in update mode so the buyer can log in to their bank again. Update mode keeps
 * the existing Plaid connection, so there is no public token to exchange afterwards.
 */
export const openPlaidUpdateMode = async (
    linkToken: string,
    onSuccess: () => void,
    onExit: () => void
): Promise<void> => {
    await loadPlaidScript();
    const plaid = (window as unknown as { Plaid?: PlaidInstance }).Plaid;
    if (!plaid) {
        throw new Error('Could not load Plaid Link. Please try again later.');
    }
    plaid.create({ token: linkToken, onSuccess: () => onSuccess(), onExit }).open();
};

export default class PlaidService {
    env: PlaidEnv;
    plaidHandler: PlaidHandler | undefined;
    // Settles once Link is loaded and configured, including on failure; never rejects.
    ready: Promise<void>;
    private isReady = false;

    constructor(
        env: PlaidEnv,
        authority: PlaidAuthority,
        onSuccess: (paymentMethod: Record<string, unknown>) => void,
        onFail: (err: Record<string, unknown> | AchFailureResult | Error) => void
    ) {
        this.env = env;

        this.ready = this.loadPlaid(authority, onSuccess, onFail);
    }

    loadPlaid = (
        authority: PlaidAuthority,
        onSuccess: (paymentMethod: Record<string, unknown>) => void,
        onFail: (err: Record<string, unknown> | AchFailureResult | Error) => void
    ): Promise<void> => {
        return loadPlaidScript()
            .then(() => this.configurePlaid(authority, onSuccess, onFail))
            .catch((err: Error) => onFail(err))
            .then(() => {
                this.isReady = true;
            });
    }

    configurePlaid = async (
        authority: PlaidAuthority,
        onSuccess: (paymentMethod: Record<string, unknown>) => void,
        onFail: (err: Record<string, unknown> | AchFailureResult | Error) => void
    ) => {
        Busy.set(true);
        const plaidLinkToken = await (requestPlaidLinkToken() as Promise<{ text: string }>)
          .then((resp) => resp.text)
          .catch((err: Error) => {
              onFail(err);
              return undefined;
          })
          .finally(() => Busy.set(false));

        if (!plaidLinkToken) {
            return;
        }

        const plaid = (window as unknown as { Plaid?: PlaidInstance }).Plaid;
        this.plaidHandler = plaid?.create({
            token: plaidLinkToken,
            onSuccess: (public_token: string, metadata: PlaidLinkOnSuccessMetadata) => {
                Busy.set(true);
                const selectedAccount = metadata.accounts?.[0];
                if (!selectedAccount) {
                    Busy.set(false);
                    onFail({ message: 'No bank account was selected in Plaid.' });
                    return;
                }
                this._handlePlaidSuccess({
                    plaidAccountId: selectedAccount.id,
                    plaidPublicToken: public_token,
                }, onSuccess, onFail);
            },
            onExit: () => {
                // todo za -- implement?
            }
        });
    }

    _handlePlaidSuccess = async (
        data: PlaidSuccessData,
        onSuccess: (paymentMethod: Record<string, unknown>) => void,
        onFail: (err: Record<string, unknown> | AchFailureResult | Error) => void
    ) => {
        try {
            const response = await requestAchSetupIntent({
                plaidPublicToken: data.plaidPublicToken,
                plaidAccountId: data.plaidAccountId,
            });

            if (response.status === 'succeeded') {
                Busy.set(false);
                onSuccess(response.paymentMethod);
            } else if (response.status === 'requires_action') {
                Busy.set(false);
                onFail({
                    requiresAction: true,
                    clientSecret: response.clientSecret,
                    message: 'Your bank requires additional verification. Please try again or contact support.',
                });
            } else {
                Busy.set(false);
                onFail({ message: `Unexpected setup status: ${response.status}` });
            }
        } catch (err) {
            Busy.set(false);
            onFail(err as Error);
        }
    }

    openPlaidHandler = async () => {
        if (!this.isReady) {
            Busy.set(true);
            await this.ready;
            Busy.set(false);
        }
        if(this.plaidHandler) {
            this.plaidHandler.open();
        } else {
            console.warn("Attempting to open plaid handler with no viable instance");
        }
    }

    closePlaidHandler = () => {
        if(this.plaidHandler) {
            this.plaidHandler.exit();
        }
    }
}
